'use client';

import { RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { useSphereHandle } from '../sphereContext';
import { CarouselHost, DragSurface, TagCard } from '../styles';
import { useCarouselDriver } from '../useCarouselDriver';
import { sentenceCase } from '../weather/insightDeck';
import type { DeckCard } from '../weather/types';

export type DragCarouselProps = {
  deck: DeckCard[];
  orbWrapRef: RefObject<HTMLDivElement | null>;
  active: boolean;
  reducedMotion: boolean;
  onPageChange: (index: number) => void;
  onUserRotate: () => void;
};

/** Act 4 — the 3D tag carousel riding the orb, plus its gesture layer. */
export const DragCarousel = ({
  deck,
  orbWrapRef,
  active,
  reducedMotion,
  onPageChange,
  onUserRotate,
}: DragCarouselProps) => {
  const sphereRef = useSphereHandle();
  const cardRefs = useRef<Array<HTMLDivElement | null>>([]);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const [grabbing, setGrabbing] = useState(false);

  const cardRefSetters = useMemo(
    () =>
      deck.map((_, i) => (el: HTMLDivElement | null) => {
        cardRefs.current[i] = el;
      }),
    [deck],
  );

  const getRadiusPx = () => {
    const rect = orbWrapRef.current?.getBoundingClientRect();
    return rect ? (rect.width / 2) * 0.92 : 120;
  };

  const driver = useCarouselDriver({
    count: deck.length || 1,
    cardRefs,
    getRadiusPx,
    onPageChange,
    onYawChange: (yaw) => sphereRef.current?.setYaw(yaw),
    reducedMotion,
  });
  const driverRef = useRef(driver);
  driverRef.current = driver;

  // Ref'd so the gesture effect below never re-runs mid-drag on a new callback
  // identity from a parent re-render.
  const onUserRotateRef = useRef(onUserRotate);
  onUserRotateRef.current = onUserRotate;

  useEffect(() => {
    if (!active) return;
    const id = window.setTimeout(() => driverRef.current.playHint(), 1600);
    return () => window.clearTimeout(id);
  }, [active]);

  useEffect(() => {
    const surface = surfaceRef.current;
    if (!surface || !active) return;

    let activeId: number | null = null;
    let px = 0;
    let lastX = 0;
    let lastT = 0;
    let velocity = 0;
    let downAt = 0;
    let moved = 0;
    let latestClientX = 0;
    let moveRaf: number | null = null;

    const toUV = (clientX: number, clientY: number): [number, number] => {
      const rc = surface.getBoundingClientRect();
      return [
        ((clientX - rc.left) / rc.width - 0.5) * 2,
        (0.5 - (clientY - rc.top) / rc.height) * 2,
      ];
    };

    const applyMove = () => {
      moveRaf = null;
      driverRef.current.move(latestClientX - px);
    };

    const onPointerDown = (e: PointerEvent) => {
      if (activeId !== null) return; // one finger drives the sphere
      activeId = e.pointerId;
      setGrabbing(true);
      driverRef.current.begin();
      onUserRotateRef.current();
      px = e.clientX;
      lastX = e.clientX;
      latestClientX = e.clientX;
      lastT = performance.now();
      velocity = 0;
      downAt = lastT;
      moved = 0;
    };
    const onPointerMove = (e: PointerEvent) => {
      if (e.pointerId !== activeId) return;
      const now = performance.now();
      const dx = e.clientX - lastX;
      const dt = Math.max(1, now - lastT);
      velocity = velocity * 0.7 + (dx / dt) * 1000 * 0.3;
      lastX = e.clientX;
      lastT = now;
      moved = Math.max(moved, Math.abs(e.clientX - px));
      latestClientX = e.clientX;
      // One driver.move() per frame however fast the pointer stream fires (120Hz).
      if (moveRaf === null) {
        moveRaf = requestAnimationFrame(applyMove);
      }
    };
    const onPointerUp = (e: PointerEvent) => {
      if (e.pointerId !== activeId) return;
      activeId = null;
      setGrabbing(false);
      if (moveRaf !== null) {
        cancelAnimationFrame(moveRaf);
        moveRaf = null;
        driverRef.current.move(latestClientX - px);
      }
      if (moved < 6 && performance.now() - downAt < 300) {
        sphereRef.current?.triggerRipple(toUV(e.clientX, e.clientY));
        return;
      }
      driverRef.current.end(velocity);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        onUserRotateRef.current();
        driverRef.current.nudge(1);
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        onUserRotateRef.current();
        driverRef.current.nudge(-1);
      }
    };

    surface.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
    surface.addEventListener('keydown', onKeyDown);
    return () => {
      if (moveRaf !== null) cancelAnimationFrame(moveRaf);
      surface.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
      surface.removeEventListener('keydown', onKeyDown);
    };
    // Deps are `[active]` on purpose: the callbacks are read through refs so the
    // listeners attach once per transition and never tear down mid-gesture.
  }, [active]);

  return (
    <>
      <CarouselHost $visible={active} aria-hidden="true">
        {deck.map((card, i) => (
          <TagCard key={card.tag} ref={cardRefSetters[i]}>
            {sentenceCase(card.tag)}
          </TagCard>
        ))}
      </CarouselHost>
      <DragSurface
        ref={surfaceRef}
        role="group"
        aria-label="Insight carousel. Use the left and right arrow keys to turn the UltraSphere."
        tabIndex={active ? 0 : -1}
        $active={active}
        $grabbing={grabbing}
      />
    </>
  );
};
